import {Breakpoint} from '../types/Breakpoint';
import {ResponsiveValue, isResponsiveValue} from '../types/ResponsiveValue';

/**
 * Converts a responsive value object to mobile-first Tailwind classes
 * @param value - Responsive value object or simple value
 * @param classPrefix - Tailwind class prefix (e.g., 'p', 'flex', 'gap')
 * @returns Space-separated Tailwind classes with breakpoint prefixes
 * @example getResponsiveClasses({ base: 2, sm: 3, lg: 5 }, 'p') → 'p-2 sm:p-3 lg:p-5'
 * @example getResponsiveClasses({ base: 'column', lg: 'row' }, 'flex') → 'flex-column lg:flex-row'
 * @example getResponsiveClasses(4, 'p') → 'p-4'
 */
export const getResponsiveClasses = <T>(
  value: T | ResponsiveValue<T>,
  classPrefix: string
): string => {
  if (!isResponsiveValue(value)) {
    return `${classPrefix}-${value}`;
  }

  const breakpoints: Breakpoint[] = ['base', 'sm', 'md', 'lg'];
  const classes: string[] = [];

  for (const breakpoint of breakpoints) {
    const breakpointValue = value[breakpoint];
    if (breakpointValue !== undefined) {
      if (breakpoint === 'base') {
        classes.push(`${classPrefix}-${breakpointValue}`);
      } else {
        classes.push(`${breakpoint}:${classPrefix}-${breakpointValue}`);
      }
    }
  }

  return classes.join(' ');
};

/**
 * Converts a responsive value object to mobile-first Tailwind classes without a prefix
 * Useful for classes that don't follow the prefix-value pattern
 * @example getResponsiveClassesNoPrefixgetResponsiveClassesWithoutPrefix({ base: 'flex', lg: 'hidden' }) → 'flex lg:hidden'
 */
export const getResponsiveClassesWithoutPrefix = <T extends string>(
  value: T | ResponsiveValue<T>
): string => {
  if (!isResponsiveValue(value)) {
    return value;
  }

  const breakpoints: Breakpoint[] = ['base', 'sm', 'md', 'lg'];
  const classes: string[] = [];

  for (const breakpoint of breakpoints) {
    const breakpointValue = value[breakpoint];
    if (breakpointValue !== undefined) {
      if (breakpoint === 'base') {
        classes.push(breakpointValue);
      } else {
        classes.push(`${breakpoint}:${breakpointValue}`);
      }
    }
  }

  return classes.join(' ');
};
